// Centralized Roles Registry (single source of truth)
// Responsibilities:
// - Fetch and cache roles list with TTL
// - Deduplicate concurrent identical requests
// - Expose simple imperative API and a React hook for components

import { useEffect, useState } from 'react';
import { apiCall } from '../services/api.service';

type Role = {
  id: number;
  code: string;
  name: string;
  description?: string;
  [key: string]: any;
};

type RolesMeta = {
  fetchedAt: number;
  expiresAt: number;
};

// TTL: 10 minutes (roles change very infrequently)
const ROLES_TTL_MS = 10 * 60 * 1000;

// Default roles fallback
const DEFAULT_ROLES: Role[] = [
  { id: 1, code: 'super_user', name: 'Super User', description: 'User with administrator privileges' },
  { id: 2, code: 'admin', name: 'Administrator', description: 'System administrator' },
  { id: 3, code: 'user', name: 'User', description: 'Standard user' },
];

// Internal store state
const state = {
  roles: [] as Role[],
  meta: null as RolesMeta | null,
  inFlight: null as Promise<Role[]> | null,
  subscribers: new Set<() => void>(),
  loading: false,
  error: null as string | null,
};

function notify() {
  state.subscribers.forEach((cb) => {
    try { cb(); } catch {}
  });
}

function now() {
  return Date.now();
}

function isFresh(): boolean {
  if (!state.meta) return false;
  return state.meta.expiresAt > now();
}

const PUBLIC_PREFIXES = ['/onboarding', '/pub', '/flows', '/firebase-login', '/store/'];

function isPublicRoute(): boolean {
  return typeof window !== 'undefined' &&
    PUBLIC_PREFIXES.some(p => window.location.pathname.startsWith(p));
}

async function fetchRoles(): Promise<Role[]> {
  if (isPublicRoute()) {
    return DEFAULT_ROLES;
  }
  try {
    const data = await apiCall('roles');
    if (data && Array.isArray(data) && data.length > 0) {
      return data;
    }
    console.warn('[RolesRegistry] No valid roles from API, using defaults');
    return DEFAULT_ROLES;
  } catch (error: any) {
    console.error('[RolesRegistry] Error fetching roles:', error);
    return DEFAULT_ROLES;
  }
}

export const RolesRegistry = {
  /**
   * Get roles list (cached, with TTL)
   */
  async getRoles(forceRefresh = false): Promise<Role[]> {
    // Return cached if fresh and not forcing refresh
    if (!forceRefresh && isFresh() && state.roles.length > 0) {
      return state.roles;
    }

    // Deduplicate concurrent requests
    if (state.inFlight) {
      return state.inFlight;
    }

    // Start fetch
    state.loading = true;
    state.error = null;
    const promise = fetchRoles().then((roles) => {
      state.roles = roles;
      state.meta = {
        fetchedAt: now(),
        expiresAt: now() + ROLES_TTL_MS,
      };
      state.inFlight = null;
      state.loading = false;
      notify();
      return roles;
    }).catch((error) => {
      state.error = error.message || 'Failed to fetch roles';
      state.inFlight = null;
      state.loading = false;
      notify();
      // Return defaults on error
      state.roles = DEFAULT_ROLES;
      return DEFAULT_ROLES;
    });

    state.inFlight = promise;
    return promise;
  },

  /**
   * Get role by code
   */
  async getRoleByCode(code: string): Promise<Role | null> {
    const roles = await this.getRoles();
    return roles.find(r => r.code === code) || null;
  },

  /**
   * Get role by ID
   */
  async getRoleById(id: number): Promise<Role | null> {
    const roles = await this.getRoles();
    return roles.find(r => r.id === id) || null;
  },

  /**
   * Invalidate cache (force refresh on next getRoles call)
   */
  invalidate() {
    state.meta = null;
    state.roles = [];
    notify();
  },

  /**
   * Clear all cache
   */
  clearCache() {
    state.meta = null;
    state.roles = [];
    state.inFlight = null;
    state.error = null;
    notify();
  },

  /**
   * Get current state (for debugging)
   */
  getState() {
    return {
      roles: state.roles,
      loading: state.loading,
      error: state.error,
      isFresh: isFresh(),
    };
  },
};

/**
 * React hook to use roles
 */
export function useRoles() {
  const [, setTick] = useState(0);

  useEffect(() => {
    const update = () => setTick(t => t + 1);
    state.subscribers.add(update);
    
    // Load roles on mount if not cached (skip on public routes)
    if (!isFresh() && !state.inFlight && !isPublicRoute()) {
      RolesRegistry.getRoles();
    }

    return () => {
      state.subscribers.delete(update);
    };
  }, []);

  return {
    roles: state.roles,
    loading: state.loading,
    error: state.error,
    refresh: () => RolesRegistry.getRoles(true),
  };
}

